import { useState, useEffect } from 'react';
import { Eye, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { API, getAuthHeader } from './api';

export function RegistrationsManagement() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReg, setSelectedReg] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSector, setFilterSector] = useState('all');

  useEffect(() => { fetchRegistrations(); }, []);

  const fetchRegistrations = async () => {
    try { const response = await axios.get(`${API}/startup-registrations`, { headers: getAuthHeader() }); setRegistrations(response.data); }
    catch { /* silent */ }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/startup-registrations/${id}/status?new_status=${status}`, {}, { headers: getAuthHeader() });
      toast.success(`Status updated to ${status}`);
      fetchRegistrations();
    } catch (error) { toast.error('Failed to update status'); }
  };

  const viewDetails = async (reg) => {
    try {
      const response = await axios.get(`${API}/startup-registrations/${reg.id}`, { headers: getAuthHeader() });
      setSelectedReg(response.data);
    } catch (error) { toast.error('Failed to load details'); }
  };

  const resetPin = async (id) => {
    if (!window.confirm('Reset this startup\'s PIN? They will need the new PIN to book advisors.')) return;
    try {
      const response = await axios.put(`${API}/startup-registrations/${id}/reset-pin`, {}, { headers: getAuthHeader() });
      toast.success(`PIN reset successfully. New PIN: ${response.data.new_pin}`);
      fetchRegistrations();
    } catch (error) { toast.error('Failed to reset PIN'); }
  };

  const deleteRegistration = async (id, name) => {
    if (!window.confirm(`Delete registration for "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/startup-registrations/${id}`, { headers: getAuthHeader() });
      toast.success('Registration deleted');
      fetchRegistrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete registration');
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    reviewed: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700'
  };

  const filteredRegistrations = registrations.filter(reg => {
    const matchesSearch = searchQuery === '' ||
      reg.startup_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      reg.founder_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      reg.founder_email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || reg.status === filterStatus;
    const matchesSector = filterSector === 'all' || reg.sector === filterSector;
    return matchesSearch && matchesStatus && matchesSector;
  });

  const sectors = [...new Set(registrations.map(r => r.sector))];

  return (
    <div>
      <h1 className="text-3xl font-bold text-[#231F20] mb-6 font-['Bricolage_Grotesque']">Startup Registrations</h1>
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <input type="text" placeholder="Search by startup name, founder, or email..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-4 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#075FA3]" data-testid="reg-search-input" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full md:w-40 h-10" data-testid="reg-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {['pending', 'reviewed', 'approved', 'rejected'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterSector} onValueChange={setFilterSector}>
          <SelectTrigger className="w-full md:w-40 h-10" data-testid="reg-sector-filter"><SelectValue placeholder="Sector" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sectors</SelectItem>
            {sectors.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <p className="text-sm text-[#58595B] mb-3">{filteredRegistrations.length} of {registrations.length} registrations</p>
      <Card>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Startup</TableHead><TableHead>Founder</TableHead><TableHead>PIN</TableHead><TableHead>Sector</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {filteredRegistrations.map((reg) => (
              <TableRow key={reg.id}>
                <TableCell className="font-medium">{reg.startup_name}</TableCell>
                <TableCell>{reg.founder_name}</TableCell>
                <TableCell><code className="bg-gray-100 px-2 py-1 rounded text-[#C83041] font-bold">{reg.pin}</code></TableCell>
                <TableCell>{reg.sector}</TableCell>
                <TableCell><Badge className={statusColors[reg.status]}>{reg.status}</Badge></TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => viewDetails(reg)}><Eye className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => resetPin(reg.id)} title="Reset PIN"><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteRegistration(reg.id, reg.startup_name)} title="Delete"><Trash2 className="w-4 h-4" /></Button>
                    <Select value={reg.status} onValueChange={(v) => updateStatus(reg.id, v)}>
                      <SelectTrigger className="w-28 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{['pending', 'reviewed', 'approved', 'rejected'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filteredRegistrations.length === 0 && !loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-[#58595B] py-8">{registrations.length === 0 ? 'No registrations yet' : 'No registrations match your filters'}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={!!selectedReg} onOpenChange={() => setSelectedReg(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedReg?.startup_name}</DialogTitle>
            <DialogDescription>Registration Details</DialogDescription>
          </DialogHeader>
          {selectedReg && (
            <div className="space-y-6 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs text-gray-500">Founder</Label><p>{selectedReg.founder_name}</p></div>
                <div><Label className="text-xs text-gray-500">Email</Label><p>{selectedReg.founder_email}</p></div>
                <div><Label className="text-xs text-gray-500">Phone</Label><p>{selectedReg.founder_phone || 'N/A'}</p></div>
                <div><Label className="text-xs text-gray-500">Affiliation</Label><p>{selectedReg.affiliation}</p></div>
              </div>
              <div><Label className="text-xs text-gray-500">Tagline</Label><p>{selectedReg.tagline}</p></div>
              <div><Label className="text-xs text-gray-500">About</Label><p className="whitespace-pre-wrap">{selectedReg.detailed_description}</p></div>
              <div><Label className="text-xs text-gray-500">Problem</Label><p className="whitespace-pre-wrap">{selectedReg.problem}</p></div>
              <div><Label className="text-xs text-gray-500">Solution</Label><p className="whitespace-pre-wrap">{selectedReg.solution}</p></div>
              <div><Label className="text-xs text-gray-500">Target Customer</Label><p className="whitespace-pre-wrap">{selectedReg.target_customer}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs text-gray-500">Stage</Label><p>{selectedReg.stage}</p></div>
                <div><Label className="text-xs text-gray-500">Sector</Label><p>{selectedReg.sector}</p></div>
              </div>
              <div><Label className="text-xs text-gray-500">Help Needed</Label><div className="flex flex-wrap gap-2 mt-1">{selectedReg.help_needed?.map(h => <Badge key={h} variant="outline">{h}</Badge>)}</div></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
